import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAsset } from '@/utils/graphqlClient';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Standardized Asset interface - single source of truth
export interface Asset {
  id: string;
  collection_id?: string;
  type?: string;
  page_id?: string;
  slug?: string;
  version?: string;
  header?: string;
  subheader?: string;

  // View configuration - standardized names
  comparison_mode: 'single' | 'side_by_side' | 'stacked';

  // Primary image URLs
  primary_original_url?: string;
  primary_cropped_url?: string;
  primary_image?: string; // Legacy support

  // Secondary image URLs
  secondary_original_url?: string;
  secondary_cropped_url?: string;
  secondary_version?: string;

  // State data
  primary_viewport_state?: any;
  secondary_viewport_state?: any;

  // Editor configuration
  crop_aspect_ratio?: string;

  // Metadata
  is_favorited?: boolean;
  created_at?: string;
  updated_at?: string;
  company_slug?: string;
  logo_url?: string;
}

// Update input type - matches what we can actually update
export interface AssetUpdateInput {
  collection_id?: string;
  comparison_mode?: 'single' | 'side_by_side' | 'stacked';
  header?: string;
  subheader?: string;
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_viewport_state?: any;
  secondary_viewport_state?: any;
  crop_aspect_ratio?: string; // Add back for saving aspect ratio
  // Add other updateable fields as needed
}

/**
 * Unified Asset Hook - Single Source of Truth
 *
 * This hook provides:
 * - Consistent asset data across all components
 * - Automatic cache synchronization
 * - Optimistic updates
 * - Error handling
 * - Loading states
 */
export function useAsset(assetId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Query for asset data - always fetch fresh viewport states using direct DB query
  const query = useQuery({
    queryKey: ['asset', assetId],
    queryFn: async (): Promise<Asset> => {
      try {
        console.log('🔄 useAsset: Fetching fresh asset data from DB for viewport states');

        // Use direct database query instead of GraphQL
        const { data, error } = await supabase
          .from('app_assets')
          .select(`
            *,
            page:page_id (
              id,
              pricing_page_url,
              company:company_id (
                id,
                slug,
                logo_url
              )
            )
          `)
          .eq('id', assetId)
          .single();

        if (error) throw error;

        // Transform the data to match the Asset interface
        const asset: Asset = {
          ...data,
          company_slug: data.page?.company?.slug,
          logo_url: data.page?.company?.logo_url,
          comparison_mode: data.comparison_mode || 'single',
        };

        return asset;
      } catch (error) {
        console.error('❌ useAsset: Failed to fetch asset:', error);
        throw error;
      }
    },
    enabled: !!assetId,
    staleTime: 0, // Always refetch viewport states
    cacheTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Mutation for updating asset - bypass cache for viewport states
  const updateMutation = useMutation({
    mutationFn: async (input: AssetUpdateInput): Promise<Asset> => {
      try {
        console.log('💾 useAsset: COMPLETE DATA BEING SENT TO DATABASE:', input);
        console.log('💾 useAsset: Viewport states being saved:', {
          primary_viewport_state: input.primary_viewport_state,
          secondary_viewport_state: input.secondary_viewport_state
        });
        const response = await updateAsset(assetId, input);
        console.log('✅ useAsset: Database response received:', response);
        return response.updateAsset as Asset;
      } catch (error) {
        console.error('❌ useAsset: Failed to update asset:', error);
        throw error;
      }
    },
    onMutate: async (newData) => {
      // For viewport states, bypass optimistic updates to ensure DB sync
      if (newData.primary_viewport_state || newData.secondary_viewport_state) {
        console.log('⚠️ useAsset: Skipping optimistic update for viewport states');
        return { previousAsset: null };
      }

      // Cancel outgoing refetches
      await queryClient.cancelQueries(['asset', assetId]);

      // Snapshot previous value
      const previousAsset = queryClient.getQueryData<Asset>(['asset', assetId]);

      // Optimistically update cache for non-viewport updates
      if (previousAsset) {
        queryClient.setQueryData<Asset>(['asset', assetId], {
          ...previousAsset,
          ...newData,
        });
      }

      return { previousAsset };
    },
    onError: (error, newData, context) => {
      // Rollback optimistic update
      if (context?.previousAsset) {
        queryClient.setQueryData(['asset', assetId], context.previousAsset);
      }

      toast({
        title: "Update Failed",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    },
    onSuccess: (updatedAsset, variables) => {
      // For viewport states, always refetch from DB instead of using cache
      if (variables.primary_viewport_state || variables.secondary_viewport_state) {
        console.log('🔄 useAsset: Forcing fresh fetch after viewport state update');
        queryClient.invalidateQueries(['asset', assetId]);
        query.refetch();
      } else {
        // For other updates, use optimistic cache update
        queryClient.setQueryData(['asset', assetId], updatedAsset);
      }

      // Always invalidate collections queries to refresh asset cards
      queryClient.invalidateQueries(['collections']);
      queryClient.invalidateQueries(['assets']);

      toast({
        title: "Asset Updated",
        description: "Changes saved successfully."
      });
    },
    onSettled: (data, error, variables) => {
      // Always refetch viewport states to ensure DB consistency
      if (variables.primary_viewport_state || variables.secondary_viewport_state) {
        console.log('🔄 useAsset: Final refetch for viewport state consistency');
        queryClient.invalidateQueries(['asset', assetId]);
      }
    },
  });

  return {
    // Data
    asset: query.data,

    // Query states
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,

    // Mutation states
    isSaving: updateMutation.isLoading,

    // Actions
    updateAsset: updateMutation.mutate,
    refetch: query.refetch,
  };
}

/**
 * Helper functions for working with asset data
 */
export const assetHelpers = {
  // Get the best available display URL for primary image
  getPrimaryDisplayUrl: (asset: Asset): string => {
    return asset.primary_cropped_url ||
           asset.primary_original_url ||
           asset.primary_image ||
           '';
  },

  // Get the best available display URL for secondary image
  getSecondaryDisplayUrl: (asset: Asset): string => {
    return asset.secondary_cropped_url ||
           asset.secondary_original_url ||
           '';
  },

  // Check if asset has secondary image
  hasSecondaryImage: (asset: Asset): boolean => {
    return !!(asset.secondary_original_url ||
              asset.secondary_cropped_url);
  },

  // Get available view modes based on images
  getAvailableViewModes: (asset: Asset): Array<'single' | 'side_by_side' | 'stacked'> => {
    const hasSecondary = assetHelpers.hasSecondaryImage(asset);
    return hasSecondary ? ['side_by_side', 'stacked'] : ['single'];
  },

  // Validate view mode against available images
  getValidViewMode: (asset: Asset, requestedMode?: string): 'single' | 'side_by_side' | 'stacked' => {
    const available = assetHelpers.getAvailableViewModes(asset);

    if (requestedMode && available.includes(requestedMode as any)) {
      return requestedMode as any;
    }

    return asset.comparison_mode || available[0];
  }
};